import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Pencil, Check } from "lucide-react";
import { resolveImg } from "@/lib/api";

export const MAX_CUSTOMIZATION_LENGTH = 300;

/**
 * Asks for whatever the product still needs before it can go into the quote
 * request: which model, and any personalisation.
 *
 * Only the missing parts are asked for - picking a model on the product page
 * and then being asked for it again in a dialog would be silly.
 */
export const ProductOptionsDialog = ({ product, preset = {}, onCancel, onConfirm }) => {
  const [variant, setVariant] = useState("");
  const [customization, setCustomization] = useState("");

  const variants = product?.variants || [];
  const needsVariant = variants.length > 0 && !preset.variant;
  const needsCustomization = !!product?.is_customizable && !preset.customization;

  useEffect(() => {
    setVariant("");
    setCustomization("");
  }, [product]);

  const trimmed = customization.trim();
  const ready = (!needsVariant || variant) && (!needsCustomization || trimmed);

  const submit = (e) => {
    e.preventDefault();
    if (!ready) return;
    onConfirm({
      variant: preset.variant || variant,
      customization: preset.customization || trimmed,
    });
  };

  return (
    <AnimatePresence>
      {product && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onCancel} data-testid="options-overlay"
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            data-testid="options-dialog"
            className="fixed inset-0 z-[60] grid place-items-center p-4 pointer-events-none"
          >
            <form
              onSubmit={submit}
              onClick={(e) => e.stopPropagation()}
              className="pointer-events-auto w-full max-w-md bg-card rounded-[2rem] border border-border shadow-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card rounded-t-[2rem]">
                <h2 className="font-display font-bold text-lg">{product.name}</h2>
                <button type="button" onClick={onCancel} data-testid="options-cancel"
                  className="p-2 rounded-full hover:bg-muted"><X size={18} /></button>
              </div>

              <div className="p-5 space-y-5">
                {needsVariant && (
                  <div>
                    <p className="text-sm font-semibold mb-3">Escolhe o modelo *</p>
                    <div className="grid grid-cols-3 gap-3">
                      {variants.map((v) => (
                        <button
                          key={v.label}
                          type="button"
                          data-testid={`variant-${v.label}`}
                          onClick={() => setVariant(v.label)}
                          className={`rounded-2xl border-2 overflow-hidden text-left transition-colors ${
                            variant === v.label ? "border-primary" : "border-border hover:border-muted-foreground/40"
                          }`}
                        >
                          <div className="aspect-square bg-muted relative">
                            {v.image && <img src={resolveImg(v.image)} alt={v.label} className="w-full h-full object-cover" />}
                            {variant === v.label && (
                              <span className="absolute top-1 right-1 grid place-items-center w-5 h-5 rounded-full brand-gradient text-white">
                                <Check size={12} />
                              </span>
                            )}
                          </div>
                          <p className="text-xs font-semibold p-2 leading-tight">{v.label}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {needsCustomization && (
                  <div>
                    <label className="text-sm font-semibold" htmlFor="customization-input">
                      <Pencil size={13} className="inline mr-1 -mt-0.5" />
                      {product.customization_label || "Indica os detalhes da personalização"} *
                    </label>
                    <textarea
                      id="customization-input"
                      data-testid="customization-input"
                      rows={3}
                      autoFocus={!needsVariant}
                      value={customization}
                      maxLength={MAX_CUSTOMIZATION_LENGTH}
                      onChange={(e) => setCustomization(e.target.value)}
                      className="w-full mt-2 rounded-2xl border border-border bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                    />
                    <p className="text-xs text-muted-foreground mt-1 text-right">
                      {customization.length}/{MAX_CUSTOMIZATION_LENGTH}
                    </p>
                  </div>
                )}
              </div>

              <div className="p-5 border-t border-border flex gap-3 sticky bottom-0 bg-card rounded-b-[2rem]">
                <button type="button" onClick={onCancel}
                  className="rounded-full border border-border px-5 py-3 font-semibold hover:bg-muted transition-colors">
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="options-confirm"
                  disabled={!ready}
                  className="flex-1 rounded-full brand-gradient text-white font-bold py-3 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:hover:scale-100"
                >
                  Adicionar ao orçamento
                </button>
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
